import { Component } from "react";
import assign from "object-assign";
import autoBind from "react-autobind";
import MobileNavBar from "./MobileNavBar";
import NavItem from "./NavItem";
import { withRouting } from "@/components/shared/withRouting";
import { Link } from "react-router-dom";
import Avatar from "react-avatar";
import Alert from "@/components/shared/Alert";
import { Icon } from "@/components/shared/Icon";

class NavBar extends Component {
  constructor() {
    super();
    this.state = {
      userDropdownActive: false,
      mobileNavIsOpen: false
    };
    autoBind(this);
  }

  closeUserDropdown = () => {
    this.setState({
      userDropdownActive: false
    });
  };

  handleUserDropdownKeyDown = e => {
    if (this.state.userDropdownActive && e.key === "Escape") {
      this.closeUserDropdown();
    }
  };

  componentDidMount() {
    document.addEventListener("keydown", this.handleUserDropdownKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleUserDropdownKeyDown);
  }

  isActive = (pathname = "") => {
    return (item = {}) => {
      if (!item.linkTo) {
        return false;
      }
      if (item.linkTo === "/") {
        return pathname.startsWith("/apps");
      }
      if (item.linkTo === "/team/members") {
        return pathname.startsWith("/team");
      }
      if (item.linkTo === "/compatibility-matrix/overview") {
        return pathname.startsWith("/compatibility-matrix");
      }
      if (item.linkTo === "/notifications") {
        return pathname.startsWith("/notifications");
      }
      return pathname.startsWith(`${item.linkTo}`);
    };
  };

  getNavItems = () => {
    const { twoFactorEnforced, twoFactorEnabled, teamEntitlements } = this.props;
    const enforced2fa = twoFactorEnforced && !twoFactorEnabled;
    if (enforced2fa) {
      return [null, null];
    }
    const hasADP = teamEntitlements?.application_distribution_platform !== false;
    if (!hasADP) {
      return [
        {
          linkTo: "/compatibility-matrix/overview",
          label: "Compatibility Matrix",
          position: "left"
        }
      ];
    }
    return [
      {
        linkTo: `/`,
        label: "Applications",
        position: "left"
      },
      {
        linkTo: "/compatibility-matrix/overview",
        label: "Compatibility Matrix",
        position: "left"
      },
      {
        linkTo: "/notifications",
        label: "Notifications",
        position: "left"
      }
    ];
  };

  getStaticItems = () => {
    const { teamEntitlements, breakpoint } = this.props;
    const isMobile = breakpoint === "mobile";
    if (isMobile) {
      return [];
    }
    const hasADP = teamEntitlements?.application_distribution_platform !== false;
    return [
      {
        linkTo: hasADP ? "/support" : "/support?product=compatibility-matrix",
        label: "Support",
        position: "right"
      },
      {
        linkTo: "/team/members",
        label: "Team",
        position: "right",
        target: "_blank"
      },
      {
        href: "https://docs.replicated.com/",
        label: "Docs",
        position: "right",
        target: "_blank"
      }
    ];
  };

  handleSignupClick = () => {
    this.props.navigate("/signup");
  };

  handleLogOut = () => {
    if (this.state.mobileNavIsOpen) {
      this.setState({
        mobileNavIsOpen: false
      });
    }
    this.props.handleLogOut(true);
  };

  handleRouteChange = route => {
    if (this.state.userDropdownActive) {
      this.closeUserDropdown();
    }
    if (this.state.mobileNavIsOpen) {
      this.setState({
        mobileNavIsOpen: false
      });
    }
    this.props.navigate(route);
  };

  getUser = () => {
    const { user } = this.props;
    const isMobile = this.props.breakpoint === "mobile";
    const missingRequired2fa = user["2fa_enforced"] && !user["2fa_enabled"];

    return [
      {
        id: "userDropdown",
        className: "",
        position: "right",
        showMobileBar: false,
        icon: [
          <div key="user-wrapper" className="relative">
            {!!this.props.user?.email && (
              <>
                <Avatar
                  name={this.props.user.email}
                  size={30}
                  color="#6a77fb"
                  textSizeRatio={2}
                  key="photo"
                  className="w-[30px] h-[30px] rounded-md mr-1.5"
                />
                <span key="arrow" className="icon clickable u-dropdownArrowIcon relative transition-transform duration-200 ease-in-out"></span>
              </>
            )}
          </div>
        ],
        dropdownContent: [
          missingRequired2fa
            ? null
            : isMobile
              ? {
                className: "DropdownItem",
                label: "Team Settings",
                onClick: () => {
                  this.handleRouteChange("/team/members");
                }
              }
              : null,
          isMobile
            ? {
              className: "DropdownItem",
              label: "Support",
              onClick: () => {
                this.handleRouteChange("/support");
              }
            }
            : null,
          isMobile
            ? {
              className: "DropdownItem",
              label: "Docs",
              href: "https://docs.replicated.com/"
            }
            : null,
          missingRequired2fa
            ? null
            : {
              className: "DropdownItem",
              label: "Account Settings",
              onClick: () => {
                this.handleRouteChange("/account-settings");
              }
            },
          {
            className: "DropdownItem",
            label: "Changelog",
            href: "https://docs.replicated.com/release-notes/rn-vendor-platform"
          },
          {
            className: "DropdownItem",
            label: "Log out",
            onClick: this.handleLogOut
          }
        ]
      }
    ];
  };

  combineItems = itemsArr => {
    return itemsArr.reduce((accum, method) => accum.concat(method(this.props)), []);
  };

  onClick = item => {
    return (e, ...rest) => {
      const activeKey = `${item.label || item.id || ""}Active`;
      if (item.label === "Troubleshoot") {
        this.props.setIsTroubleshootOpenFromNavbar();
      }
      if (item.href) {
        return;
      }
      if (typeof item.onClick === "function") {
        item.onClick(e, ...rest);
        return;
      }
      this.setState({
        [activeKey]: !this.state[activeKey]
      });
    };
  };

  render() {
    const { className, user } = this.props;

    let itemsArr = [];
    const isPathActive = this.isActive(this.props.location.pathname);

    // get items
    if (user?.id) {
      itemsArr.push(this.getNavItems);
      itemsArr.push(this.getStaticItems);
      itemsArr.push(this.getUser);
    }

    // build items
    const headerItems = this.combineItems(itemsArr)
      .filter(item => item)
      .map(item =>
        assign(item, {
          isActive:
            isPathActive(item) &&
            (item.label !== "Troubleshoot" || this.props.isTroubleshootOpenedFromNavbar)
        })
      );
    const renderItem = (item, index) => {
      return (
        <NavItem
          key={index}
          {...item}
          onClick={this.onClick(item)}
          isDropDownActive={this.state[`${item.label || item.id || index || ""}Active`]}
        />
      );
    };

    const rightItems = headerItems.filter(item => item.position === "right");
    const leftItems = headerItems.filter(item => item.position === "left");

    const isMobile = this.props.breakpoint === "mobile";
    let currentApp;
    if (
      this.props.apps &&
      this.props.selectedApp &&
      this.props.apps[this.props.selectedApp]
    ) {
      currentApp =
        this.props.apps[this.props.selectedApp].slug ||
        this.props.apps[this.props.selectedApp].Slug;
    }

    return (
      <div
        className={`flex flex-none w-full py-[13px] relative bg-white shadow-[0_1px_4px_rgba(0,0,0,0.16)] z-[400] ${className || ""} ${isMobile ? "!py-2" : ""
          }`}
      >
        <div className="w-full px-5 lg:px-[30px] mx-auto box-border flex flex-1 min-h-0">
          <div className="flex-1 min-h-0 justify-start items-center">
            {isMobile ? (
              <div className="flex flex-1 min-h-0">
                <div className="relative z-[460] flex-1 min-h-0 flex flex-col items-center justify-center [&_.NavItem:first-child]:ml-0 [&_.NavItem:first-child]:pl-0">
                  <NavItem
                    aria-label={this.state.mobileNavIsOpen ? "Close menu" : "Open menu"}
                    icon={
                      <Icon
                        name={this.state.mobileNavIsOpen ? "close" : "menu"}
                        size="normal"
                        color="#6B7280"
                      />
                    }
                    onClick={() => {
                      this.setState({
                        mobileNavIsOpen: !this.state.mobileNavIsOpen
                      });
                    }}
                  />
                </div>
                <div className="flex-1 min-h-0 flex justify-center items-center">
                  {currentApp !== undefined ? (
                    <Link
                      className="flex flex-row"
                      to={`/apps/${currentApp}/channels`}
                      aria-label="Go to current application channels"
                    >
                      <span className="logo" aria-hidden="true"></span>
                    </Link>
                  ) : (
                    <Link
                      className="flex flex-row"
                      to="/"
                      aria-label="Go to applications"
                    >
                      <span className="logo" aria-hidden="true"></span>
                    </Link>
                  )}
                </div>
                <div className="flex flex-1 min-h-0 justify-end">
                  <div className="flex flex-none items-center">
                    {!!this.props.user?.email && (
                      <NavItem
                        linkTo="/account-settings/info"
                        icon={[
                          <div
                            key="user-wrapper"
                            className="relative"
                          >
                            <Avatar
                              name={this.props.user.email}
                              size={30}
                              color="#6a77fb"
                              textSizeRatio={2}
                              round={true}
                              key="photo"
                              className="w-[30px] h-[30px] rounded-md"
                            />
                          </div>
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex">
                <div className="flex flex-none">
                  <div className="mr-5 relative">
                    <div>
                      {currentApp !== undefined ? (
                        <Link
                          className="flex flex-row"
                          to={`/apps/${currentApp}/channels`}
                          aria-label="Go to current application channels"
                        >
                          <span className="logo" aria-hidden="true"></span>
                        </Link>
                      ) : (
                        <Link
                          className="flex flex-row"
                          to="/"
                          aria-label="Go to applications"
                        >
                          <span className="logo" aria-hidden="true"></span>
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-none items-stretch left-items">
                    {leftItems.map(renderItem)}
                  </div>
                </div>
                <div className="flex flex-1 min-h-0 justify-end [&_.NavItem:last-child]:mr-0 [&_.NavItem:last-child]:pr-0">
                  {this.props.err ? (
                    <Alert variant="error" className="mb-1.5">
                      {this.props.errMessage}
                    </Alert>
                  ) : null}
                  <div className="flex flex-none items-stretch">
                    {rightItems.map(renderItem)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {this.state.userDropdownActive ? (
          <div className="fixed inset-0 bg-transparent z-[401]" onClick={this.closeUserDropdown}></div>
        ) : null}
        {isMobile ? (
          <MobileNavBar
            user={user}
            className="MobileNavBar"
            items={headerItems}
            isOpen={this.state.mobileNavIsOpen}
            onClose={() => {
              this.setState({
                mobileNavIsOpen: false
              });
            }}
          />
        ) : null}
      </div>
    );
  }
}

export default withRouting(NavBar);
